import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, AlertCircle } from '../components/Icons';
import { API } from '../contexts/AuthContext';
import { ImagePicker, PickedImage } from '../components/ImagePicker';

/* ─── Smart naming ───────────────────────────────────────────── */
function resolveSmartName(base: string, existingNames: string[]): string {
  if (!existingNames.includes(base)) return base;
  let i = 2;
  while (existingNames.includes(`${base}-${i}`)) i++;
  return `${base}-${i}`;
}

function NewProject() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const prefill        = (location.state as any)?.prefillName as string | undefined;
  const inspirationUrl = (location.state as any)?.inspirationUrl as string | undefined;

  const [name, setName]         = useState('');
  const [image, setImage]       = useState<PickedImage | null>(null);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [resolving, setResolving] = useState(false);

  /* On mount, if prefill name provided, resolve smart name from existing projects */
  useEffect(() => {
    if (!prefill) return;
    setResolving(true);
    axios.get(`${API}/projects`)
      .then(res => {
        const names: string[] = res.data.map((p: any) => p.name as string);
        setName(resolveSmartName(prefill, names));
      })
      .catch(() => setName(prefill))
      .finally(() => setResolving(false));
  }, [prefill]);

  const handleImagePicked = (picked: PickedImage | null) => {
    setImage(picked);
  };

  const canSubmit = name.trim().length > 0 && !resolving;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError('');
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', name.trim());
      if (image?.file) formData.append('image', image.file);
      const res = await axios.post(`${API}/projects`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      navigate(`/project/${res.data.id}`, {
        replace: true,
        state: inspirationUrl ? { inspirationUrl } : undefined,
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create project. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div className="page-root">

      {/* Header */}
      <header className="app-header">
        <div className="header-inner">
          <button className="header-action-btn" onClick={() => navigate(-1)} aria-label="Go back">
            <ArrowLeft size={20} />
          </button>
          <div style={{ flex: 1 }} />
        </div>
      </header>

      <div className="page-scroll">
        <div className="page-container" style={{ paddingTop: 24, paddingBottom: 48 }}>

          {prefill && (
            <div className="prefill-badge">
              <span className="label-upper" style={{ fontSize: 10, color: 'var(--accent)' }}>Space selected</span>
              <h2 className="heading-lg" style={{ marginTop: 2 }}>{prefill}</h2>
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }} noValidate>

            {error && (
              <div className="alert alert-error" role="alert">
                <AlertCircle size={16} style={{ flexShrink: 0 }} /><span>{error}</span>
              </div>
            )}

            {/* Project Name */}
            <div className="form-field">
              <label className="form-label" htmlFor="projectName">
                Project Name<span className="required">*</span>
              </label>
              {resolving ? (
                <div className="form-input" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-faint)' }}>
                  <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />Checking name…
                </div>
              ) : (
                <input
                  id="projectName"
                  className="form-input"
                  type="text"
                  placeholder="e.g., Living Room Redesign"
                  value={name}
                  onChange={e => { setName(e.target.value); if (error) setError(''); }}
                  autoFocus={!prefill}
                  required
                />
              )}
              <p className="form-helper">Give your project a descriptive name.</p>
            </div>

            {/* Space Image */}
            <ImagePicker
              value={image}
              onChange={handleImagePicked}
              onRemove={() => setImage(null)}
              label="Space Image"
              hint="Add a photo of the room or space you want to transform"
              id="spaceImage"
            />

            {/* Actions */}
            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <button type="button" className="btn btn-secondary" onClick={() => navigate('/home')} style={{ flex: 1 }}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading || !canSubmit} style={{ flex: 2 }}>
                {loading
                  ? <><span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />Creating…</>
                  : 'Create Project'}
              </button>
            </div>
          </form>
        </div>
      </div>

    </div>
  );
}

export default NewProject;
